import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import {
  SEGMENTS,
  SHAPE_TRIANGLE_BUDGET,
  isShapeKind,
  shapeTriangles,
  type Lesson,
  type Segments,
  type ShapeDetail,
  type LessonObject,
  type ShapeGroup,
  type ShapeGroupInner,
  type ShapeLeaf,
  type ShapePart,
} from '@vrlearning/lesson-schema';

/**
 * Shapes: objects a lesson builds out of parts (ADR-030).
 *
 * A lesson generator can plan a volcano, a cell or a water cycle, and none of
 * them has a builder of its own. So a `shape:` object carries its own recipe
 * -- primitives, turned and extruded outlines, and groups of them -- and this
 * draws the recipe. Data, never code: whatever a model writes, the headset
 * only ever runs the geometry below.
 *
 * **One mesh per shape.** Every part is moved into the shape's own space and
 * merged, with its colour in the vertices, so a shape of sixty parts is one or
 * two draw calls rather than sixty -- the same reason bones.ts merges. It is
 * also what the rest of the engine expects: one pickable `body` for hotspots,
 * focus, the badge and the assembly bench. Parts that are see-through get a
 * material of their own, one per opacity (rounded to tenths), as groups of the
 * same geometry; so do solid parts that must be seen from behind (below), each
 * finish (matte, gloss), and glowing parts, one material per glow colour.
 *
 * **Modest detail.** Segment counts are fixed per detail level, never taken
 * from the lesson, and a whole lesson's shapes have a triangle budget: over
 * it, every shape is drawn at the simpler level instead (`shapeDetailFor`).
 */

// Segment counts, the triangle budget and the exact count live in
// lesson-schema (shape-cost.ts), so the generator's budget check counts what
// is drawn here; shapes.test.ts holds the count to the real geometry.
export type { ShapeDetail };

const DEGREES = Math.PI / 180;

/** Any part at any level; groups at the second level hold drawn parts only. */
type Part = ShapeLeaf | ShapeGroup | ShapeGroupInner;

type Finish = 'matte' | 'gloss' | 'glow';

/**
 * The pieces drawn with one material: at one opacity, from one or both sides,
 * with one finish -- and for glow, in one colour, because a material's
 * emissive is a single colour rather than one per vertex.
 */
type Draw = {
  tenths: number;
  bothSides: boolean;
  finish: Finish;
  glow?: string;
  pieces: THREE.BufferGeometry[];
};
type Pieces = Map<string, Draw>;

export type ShapeMesh = {
  body: THREE.Mesh<THREE.BufferGeometry, THREE.MeshStandardMaterial[]>;
  triangles: number;
};

/** A part's own geometry, about its own middle, before it is moved into place. */
function leafGeometry(part: ShapeLeaf, s: Segments): THREE.BufferGeometry {
  switch (part.type) {
    case 'box':
      return new THREE.BoxGeometry(part.size[0], part.size[1], part.size[2]);
    case 'sphere':
      return new THREE.SphereGeometry(part.size[0], s.round, s.rings);
    case 'cylinder':
      return new THREE.CylinderGeometry(part.size[0], part.size[1], part.size[2], s.round);
    case 'cone':
      return new THREE.ConeGeometry(part.size[0], part.size[1], s.round);
    case 'torus':
      return new THREE.TorusGeometry(part.size[0], part.size[1], s.tube, s.tubular);
    case 'capsule':
      return new THREE.CapsuleGeometry(part.size[0], part.size[1], s.cap, s.round);
    case 'plane':
      return new THREE.PlaneGeometry(part.size[0], part.size[1]);
    case 'lathe':
      // A negative distance from the axis means nothing; the schema cannot
      // say "the first number is at least 0" without a tuple, which the
      // structured-output variant would not keep (ADR-030).
      return new THREE.LatheGeometry(
        part.points.map(([r, y]) => new THREE.Vector2(Math.max(0, r), y)),
        s.lathe,
      );
    case 'extrude': {
      const outline = new THREE.Shape(part.outline.map(([x, y]) => new THREE.Vector2(x, y)));
      const geometry = new THREE.ExtrudeGeometry(outline, {
        depth: part.depth,
        bevelEnabled: false,
        steps: 1,
      });
      // Centred on its pos, like every other part, rather than starting there.
      return geometry.translate(0, 0, -part.depth / 2);
    }
  }
}

/** A part's or group's place in its parent: metres, degrees, and a group's scale. */
function placement(
  pos: readonly number[] | undefined,
  rot: readonly number[] | undefined,
  scale = 1,
): THREE.Matrix4 {
  const [x = 0, y = 0, z = 0] = pos ?? [];
  const [rx = 0, ry = 0, rz = 0] = rot ?? [];
  return new THREE.Matrix4().compose(
    new THREE.Vector3(x, y, z),
    new THREE.Quaternion().setFromEuler(new THREE.Euler(rx * DEGREES, ry * DEGREES, rz * DEGREES)),
    new THREE.Vector3(scale, scale, scale),
  );
}

/** Every vertex the part's colour, so parts of different colours can share a mesh. */
function paint(geometry: THREE.BufferGeometry, hex: string): void {
  const colour = new THREE.Color(hex);
  const count = geometry.getAttribute('position').count;
  const colours = new Float32Array(count * 3);
  for (let i = 0; i < count * 3; i += 3) {
    colours[i] = colour.r;
    colours[i + 1] = colour.g;
    colours[i + 2] = colour.b;
  }
  geometry.setAttribute('color', new THREE.BufferAttribute(colours, 3));
}

/** Below this a part covers less than a tenth of a millimetre squared: nothing to see or point at. */
const AREA_EPSILON = 1e-8;

/** Every number finite, and the triangles together covering some area. */
function visible(geometry: THREE.BufferGeometry): boolean {
  const p = geometry.getAttribute('position');
  const a = new THREE.Vector3();
  const b = new THREE.Vector3();
  const c = new THREE.Vector3();
  let area = 0;
  for (let i = 0; i + 2 < p.count; i += 3) {
    a.fromBufferAttribute(p, i);
    b.fromBufferAttribute(p, i + 1);
    c.fromBufferAttribute(p, i + 2);
    if (![a.x, a.y, a.z, b.x, b.y, b.z, c.x, c.y, c.z].every(Number.isFinite)) return false;
    area += b.sub(a).cross(c.sub(a)).length() / 2;
  }
  return area > AREA_EPSILON;
}

/**
 * A part's geometry ready to merge, or null when there is nothing to draw.
 *
 * The schema lets through outlines three.js can do little with -- one point
 * three times, a lathe that never leaves its axis -- and a model will write
 * them sooner or later. Such a part is left out rather than drawn as
 * triangles nobody can see or point at, or as NaN that would put the name
 * nowhere; if nothing is left, the shape gets the grey block below.
 */
function drawablePiece(part: ShapeLeaf, s: Segments): THREE.BufferGeometry | null {
  const outline = part.type === 'lathe' || part.type === 'extrude';
  let raw: THREE.BufferGeometry;
  try {
    raw = leafGeometry(part, s);
  } catch {
    return null;
  }
  // Non-indexed and without uvs or groups: the built-in shapes differ on all
  // three, and a merge refuses to mix them (as in bones.ts).
  const piece = raw.index ? raw.toNonIndexed() : raw;
  if (piece !== raw) raw.dispose();
  piece.deleteAttribute('uv');
  piece.clearGroups();
  // Only an outline can come out empty or odd: a primitive's sizes are at
  // least 5 mm (schema), so it always has area and finite numbers.
  if (!outline) return piece;
  if (!visible(piece)) {
    piece.dispose();
    return null;
  }
  // A repeated outline point can leave a normal of 0/0; lighting needs a number.
  const normals = piece.getAttribute('normal').array;
  for (let i = 0; i < normals.length; i++) {
    if (!Number.isFinite(normals[i])) {
      piece.computeVertexNormals();
      break;
    }
  }
  return piece;
}

function collect(parts: readonly Part[], parent: THREE.Matrix4, s: Segments, into: Pieces): void {
  for (const part of parts) {
    if (part.type === 'group') {
      collect(
        part.parts,
        parent.clone().multiply(placement(part.pos, part.rot, part.scale)),
        s,
        into,
      );
      continue;
    }
    const piece = drawablePiece(part, s);
    if (!piece) continue;
    piece.applyMatrix4(parent.clone().multiply(placement(part.pos, part.rot)));
    const finish = part.finish ?? 'matte';
    // A glowing part is black underneath: its emissive alone gives its
    // colour, which otherwise the studio's light washes out to a pastel.
    paint(piece, finish === 'glow' ? '#000000' : part.color);

    const tenths = Math.round((part.opacity ?? 1) * 10);
    // A closed solid's inside is never seen, so it is drawn from the front
    // only: half the pixels to shade, which a Quest notices. A plane, a lathe
    // open at the top, an extrusion whose outline may run either way round,
    // and anything see-through have to read from behind as well.
    const bothSides =
      tenths < 10 || part.type === 'plane' || part.type === 'lathe' || part.type === 'extrude';
    // Glow parts group by their colour: one material each, its emissive that
    // colour (the spec's "glow grouped by colour", not a shader patch).
    const glow = finish === 'glow' ? part.color.toLowerCase() : undefined;
    const key = `${tenths}/${bothSides ? 'both' : 'front'}/${finish}/${glow ?? ''}`;
    const draw: Draw = into.get(key) ?? {
      tenths,
      bothSides,
      finish,
      ...(glow ? { glow } : {}),
      pieces: [],
    };
    draw.pieces.push(piece);
    into.set(key, draw);
  }
}

/** How rough each finish is: matte is today's material; gloss catches the studio's key light. */
const ROUGHNESS: Record<Finish, number> = { matte: 0.8, gloss: 0.15, glow: 0.8 };

function material(
  opacity: number,
  bothSides = false,
  finish: Finish = 'matte',
  glow?: string,
): THREE.MeshStandardMaterial {
  const solid = opacity >= 1;
  const made = new THREE.MeshStandardMaterial({
    vertexColors: true,
    roughness: ROUGHNESS[finish],
    metalness: 0,
    side: bothSides ? THREE.DoubleSide : THREE.FrontSide,
    transparent: !solid,
    opacity,
    depthWrite: solid,
  });
  if (glow) {
    // Lit from inside in its own colour, whatever the light. The step's
    // highlight makes it glow brighter rather than white (render/motion.ts).
    made.emissive.set(glow);
    made.emissiveIntensity = 1;
    made.userData['finish'] = 'glow';
  }
  return made;
}

/** The parts of one shape as one mesh, in the shape's own space. */
export function buildShapeMesh(
  parts: readonly ShapePart[],
  detail: ShapeDetail = 'full',
): ShapeMesh {
  const pieces: Pieces = new Map();
  collect(parts, new THREE.Matrix4(), SEGMENTS[detail], pieces);

  // Solid first (front-only before both sides), then from the least to the
  // most see-through: groups are drawn in this order.
  const draws = [...pieces.values()].sort(
    (a, b) => b.tenths - a.tenths || Number(a.bothSides) - Number(b.bothSides),
  );
  const merged: THREE.BufferGeometry[] = [];
  const materials: THREE.MeshStandardMaterial[] = [];
  for (const draw of draws) {
    const one = mergeGeometries(draw.pieces, false);
    for (const piece of draw.pieces) piece.dispose();
    if (!one) continue;
    merged.push(one);
    materials.push(material(draw.tenths / 10, draw.bothSides, draw.finish, draw.glow));
  }

  let geometry = merged.length > 0 ? mergeGeometries(merged, true) : null;
  for (const one of merged) one.dispose();
  if (!geometry) {
    // Nothing drawable. The validator makes this unreachable for a lesson
    // that ships; a small grey block keeps the object pickable rather than
    // leaving a hotspot on nothing.
    geometry = new THREE.BoxGeometry(0.1, 0.1, 0.1);
    // A box comes with a group per face for six materials; this mesh has one,
    // and with an array of materials only its groups are drawn or hit.
    geometry.clearGroups();
    geometry.addGroup(0, geometry.index?.count ?? Infinity, 0);
    materials.splice(0, materials.length, material(1));
    paint(geometry, '#9aa0a8');
  }
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();

  const triangles = (geometry.index?.count ?? geometry.getAttribute('position').count) / 3;
  return { body: new THREE.Mesh(geometry, materials), triangles };
}

export type BuiltShape = ShapeMesh & {
  /** From the object's `pos`, to its farthest point: what a viewpoint stands back from. */
  radius: number;
  labelHeight: number;
  labelScale: number;
  badge: { at: [number, number, number]; size: number };
};

/** A `shape:` object, ready for the scene's registry. */
export function buildShape(object: LessonObject, detail: ShapeDetail = 'full'): BuiltShape {
  const mesh = buildShapeMesh(object.parts ?? [], detail);
  const box = mesh.body.geometry.boundingBox ?? new THREE.Box3();
  const sphere = mesh.body.geometry.boundingSphere ?? new THREE.Sphere();

  const radius = Math.max(0.05, sphere.center.length() + sphere.radius);
  // A name as wide as a planet's over a thing the size of a hand hides it.
  const labelScale = Math.min(1, Math.max(0.4, radius * 1.5));
  const labelHeight = box.max.y + 0.12;
  const size = Math.min(0.3, Math.max(0.1, radius * 0.4));
  // Just above the name (a label is 0.3 m tall at scale 1, panel.ts).
  const badge = {
    at: [0, labelHeight + 0.15 * labelScale + size / 2 + 0.05, 0] as [number, number, number],
    size,
  };

  return { ...mesh, radius, labelScale, labelHeight, badge };
}

export type ShapeBudget = {
  detail: ShapeDetail;
  /** Every shape in the lesson at full detail. */
  triangles: number;
  /** Every shape at the level they are drawn at, which may still be over the limit. */
  drawn: number;
  limit: number;
};

/**
 * How finely a lesson's shapes are drawn. Over the budget in full, every
 * shape is drawn simpler; if even that is over, they are still drawn and the
 * lesson still opens -- leaving parts out would change what a lesson shows
 * without anyone deciding it -- and `drawn` says by how much.
 */
export function shapeDetailFor(lesson: Lesson, budget = SHAPE_TRIANGLE_BUDGET): ShapeBudget {
  const shapes = Object.values(lesson.objects ?? {}).filter(
    (object) => isShapeKind(object.kind) && object.parts,
  );
  const total = (detail: ShapeDetail): number =>
    shapes.reduce((sum, object) => sum + shapeTriangles(object.parts ?? [], detail), 0);

  const triangles = total('full');
  if (triangles <= budget) return { detail: 'full', triangles, drawn: triangles, limit: budget };
  return { detail: 'simple', triangles, drawn: total('simple'), limit: budget };
}

/** 245760 as 245.760, the Dutch way, whatever the browser's own language. */
const thousands = (n: number): string =>
  String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, '.');

/** What the desktop preview says about a lesson drawn simpler, in its corner; nothing when it is not. */
export function shapeNote(budget: ShapeBudget): string | undefined {
  if (budget.detail === 'full') return undefined;
  // Short: it shares the top of a laptop window with the other corner's note.
  if (budget.drawn > budget.limit) {
    return `Vormen eenvoudiger getekend, nog te zwaar: ${thousands(budget.drawn)} driehoeken (grens ${thousands(budget.limit)})`;
  }
  return `Vormen eenvoudiger getekend: ${thousands(budget.triangles)} → ${thousands(budget.drawn)} driehoeken`;
}
